import { captureException } from '@sentry/browser';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkVariant } from 'src/api/contentful/contentTypes.cloverdotcom';
import { useCart } from 'src/components/Cart/UseCart';
import { StructuredModal } from 'src/components/StructuredModal/StructuredModal';

export interface SwapCartBundleState {
  bundleId: string;
  needsRedirect?: boolean;
  solutionTitle?: string;
}

export interface SwapCartBundleModalProps {
  bundleState: [SwapCartBundleState | undefined, (state?: SwapCartBundleState) => void];
}

function changePhoneNumberDynamically() {
  if (window.Invoca) {
    try {
      window.Invoca.PNAPI.run();
    } catch (err) {
      let msg = err.message;
      if (err.name === 'SecurityError') {
        msg += ' Cookies are most likely disabled on the browser.';
      }
      const e = new Error(msg);
      e.stack = err;
      e.name = 'InvocaLoadError';
      captureException(e);
      console.error('Unable to run Invoca', e);
    }
  }
}

export const SwapCartBundleModal: React.FC<SwapCartBundleModalProps> = ({ bundleState }) => {
  const [{ bundleId, needsRedirect, solutionTitle } = {} as SwapCartBundleState, setBundleState] = bundleState;
  const [isOpen, setIsOpen] = useState(!!bundleId);
  const { addBundleById } = useCart();
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      setTimeout(changePhoneNumberDynamically, 500);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(!!bundleId);
  }, [bundleId]);

  const onCancel = () => {
    setBundleState(undefined);
  };

  const onConfirm = () => {
    if (!bundleId) return;
    addBundleById(bundleId, { needsRedirect, solutionTitle });
  };

  return (
    <StructuredModal
      modalState={[isOpen, onCancel]}
      title={t('swap-cart-bundle.title')}
      description={t('swap-cart-bundle.description')}
      actions={[
        {
          label: t('cancel'),
          variant: LinkVariant.SECONDARY,
          action: onCancel,
        },
        {
          label: t('confirm'),
          variant: LinkVariant.PRIMARY,
          action: onConfirm,
        },
      ]}
    />
  );
};

SwapCartBundleModal.displayName = 'SwapCartBundleModal';
